import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Tags,
  ArrowLeftRight,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Target,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

const navItems = [
  // Fluxo de caixa and Orçamento are sections of Painel (`#cash-flow`, `#budget`), not destinations of their own.
  { path: '/dashboard', label: 'Painel', icon: LayoutDashboard },
  { path: '/goals', label: 'Metas', icon: Target },
  { path: '/transactions', label: 'Transações', icon: ArrowLeftRight },
  { path: '/categories', label: 'Categorias', icon: Tags },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const initials = user?.displayName
    ? user.displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? 'U';

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar — always dark regardless of theme */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-60 lg:flex-col z-40">
        <div className="flex flex-col flex-grow overflow-y-auto bg-sidebar border-r border-sidebar-border">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-6 border-b border-sidebar-border">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
              <span className="text-primary-foreground font-bold text-sm font-display">P</span>
            </div>
            <div>
              <p className="text-sidebar-foreground font-semibold text-sm leading-tight">Patri</p>
              <p className="text-sidebar-foreground/60 text-[11px] leading-tight">Finanças Pessoais</p>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 group relative',
                  isActive(item.path)
                    ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
                    : 'text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/60'
                )}
              >
                {isActive(item.path) && (
                  <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-sidebar-primary" />
                )}
                <item.icon className={cn(
                  'h-4 w-4 shrink-0 transition-colors',
                  isActive(item.path) ? 'text-sidebar-primary' : 'text-sidebar-foreground/40 group-hover:text-sidebar-foreground/70'
                )} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Bottom section */}
          <div className="px-3 pb-4 space-y-2 border-t border-sidebar-border pt-3">
            {/* User info */}
            <div className="flex items-center gap-3 px-3 py-2.5">
              <div className="h-7 w-7 rounded-full bg-sidebar-primary/20 flex items-center justify-center shrink-0">
                <span className="text-sidebar-primary text-xs font-semibold">{initials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sidebar-foreground text-xs font-medium truncate">
                  {user?.displayName || 'Usuário'}
                </p>
                <p className="text-sidebar-foreground/60 text-[11px] truncate">{user?.email}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <button
                onClick={toggleTheme}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-all"
              >
                {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                {theme === 'dark' ? 'Claro' : 'Escuro'}
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-all"
              >
                <LogOut className="h-3.5 w-3.5" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile menu outside-tap-to-close overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-sidebar border-b border-sidebar-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xs font-display">P</span>
            </div>
            <span className="text-sidebar-foreground font-semibold text-sm">Patri</span>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-1.5 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-all"
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="bg-sidebar border-b border-sidebar-border animate-slide-down">
            <nav className="px-3 py-3 space-y-0.5">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all relative',
                    isActive(item.path)
                      ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
                      : 'text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/60'
                  )}
                >
                  {isActive(item.path) && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-sidebar-primary" />
                  )}
                  <item.icon className={cn(
                    'h-4 w-4',
                    isActive(item.path) ? 'text-sidebar-primary' : 'text-sidebar-foreground/40'
                  )} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
            <div className="px-4 py-3 border-t border-sidebar-border flex gap-2">
              <Button variant="ghost" size="sm" onClick={toggleTheme} className="flex-1 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent text-xs h-8">
                {theme === 'dark' ? <Sun className="h-3.5 w-3.5 mr-1.5" /> : <Moon className="h-3.5 w-3.5 mr-1.5" />}
                {theme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="flex-1 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent text-xs h-8">
                <LogOut className="h-3.5 w-3.5 mr-1.5" />
                Sair
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="lg:pl-60">
        <div className="pt-16 lg:pt-0 min-h-screen">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};
